import {
	FieldMappingSourceType,
	SalesforceEntityType,
	SalesforceField,
	SalesforceSourceField,
} from "../../schema/salesforce";
import {
	SalesforceFieldMappingPreviewResponse,
	SalesforceTestSyncResponse,
} from "../../schema/salesforceMappingPreview";
import {
	MappingPreviewLabels,
	MappingPreviewOutcome,
	MappingTestSyncOutcome,
} from "./MappingPreviewModal";

type Ts = (key: string, options?: Record<string, unknown>) => string;

/** i18n key of the tab label shown as the "which mapping am I previewing" badge. */
const ENTITY_BADGE_KEY: Record<SalesforceEntityType, string> = {
	[SalesforceEntityType.CONTACT]: "contacts",
	[SalesforceEntityType.ACCOUNT]: "accounts",
	[SalesforceEntityType.LEAD]: "leads",
	[SalesforceEntityType.OPPORTUNITY]: "opportunities",
};

/** Labels for the preview modal, read from the Salesforce `fieldMapping` i18n block. */
export const buildSalesforceMappingPreviewLabels = (
	ts: Ts,
	entityType: SalesforceEntityType
): MappingPreviewLabels => ({
	entityBadge: ts(ENTITY_BADGE_KEY[entityType]),
	createsLabel: ts("preview.createsLabel"),
	createsDescription: ts(`preview.createsByEntity.${entityType}`),
	cancel: ts("preview.cancel"),
	title: ts("preview.title"),
	subtitle: ts("preview.subtitle"),
	prospectLabel: ts("preview.prospectLabel"),
	prospectPlaceholder: ts("preview.prospectPlaceholder"),
	run: ts("preview.run"),
	source: ts("ourField"),
	target: ts("salesforceField"),
	value: ts("preview.value"),
	emptyValue: ts("preview.emptyValue"),
	aiNotRun: ts("preview.aiNotRun"),
	inactive: ts("preview.inactive"),
	fallbacksTitle: ts("preview.fallbacksTitle"),
	fallbacksHint: ts("preview.fallbacksHint"),
	syncResolvedTitle: ts("preview.syncResolvedTitle"),
	syncResolvedHint: ts("preview.syncResolvedHint"),
	noMappings: ts("preview.noMappings"),
	error: ts("preview.error"),
	missingRequiredTitle: ts("preview.warnings.missingRequiredTitle"),
	missingRequiredHint: ts("preview.warnings.missingRequiredHint"),
	syncButton: ts("preview.sync.button"),
	syncWarning: ts(`preview.sync.warningByEntity.${entityType}`),
	syncSuccessTitle: ts("preview.sync.successTitle"),
	syncFailedTitle: ts("preview.sync.failedTitle"),
	syncOpenRecord: ts("preview.sync.openRecord"),
});

/**
 * Map the Salesforce preview response to display rows.
 *
 * No value validation happens here. The API already ran the sync's own coercion pass and the
 * value it returns is the **coerced** one — truncated to the field's `length`, dates as
 * `YYYY-MM-DD`, multi-picklists re-joined on `;` — so `skipReason` is the authoritative "this
 * will not be written" verdict. Re-deriving either in the browser could only disagree with the
 * sync, which is precisely the drift this preview exists to catch.
 *
 * `syncResolvedFields` is passed through as its own list rather than folded into `fallbacks`:
 * `AccountId` / `OwnerId` / `RecordTypeId` have no value a dry run can show, but reporting them
 * as missing required fields would be the opposite of the truth.
 */
export const buildSalesforcePreviewOutcome = (
	response: SalesforceFieldMappingPreviewResponse,
	sourceFields: SalesforceSourceField[],
	salesforceFields: SalesforceField[],
	ts: Ts
): MappingPreviewOutcome => {
	const targetLabel = (salesforceField: string, storedLabel?: string | null) =>
		salesforceFields.find((field) => field.name === salesforceField)?.label ??
		storedLabel ??
		salesforceField;

	const sourceLabel = (sourceType: FieldMappingSourceType, sourceField: string) => {
		if (sourceType === FieldMappingSourceType.CONSTANT) {
			return ts("constantLabel", { value: sourceField });
		}

		if (sourceType === FieldMappingSourceType.AI) {
			return sourceField;
		}

		return sourceFields.find((field) => field.name === sourceField)?.label ?? sourceField;
	};

	return {
		rows: response.rows.map((row) => ({
			id: row.mappingId,
			sourceLabel: sourceLabel(row.sourceType, row.sourceField),
			targetLabel: targetLabel(row.salesforceField, row.salesforceFieldLabel),
			value: row.value,
			resolved: row.resolved,
			isAiMapping: row.isAiMapping,
			isActive: row.isActive,
			warnings: row.skipReason ? [row.skipReason] : [],
		})),
		fallbacks: response.fallbacks.map((fallback) => ({
			targetLabel: targetLabel(fallback.salesforceField),
			value: fallback.value,
		})),
		syncResolved: response.syncResolvedFields.map((field) => targetLabel(field)),
		missingRequired: response.missingRequired,
	};
};

/** Map the Salesforce test-sync response to display records for the preview modal. */
export const buildSalesforceTestSyncOutcome = (
	response: SalesforceTestSyncResponse,
	ts: Ts
): MappingTestSyncOutcome => {
	// On an opportunity or lead test the deep link points at that record, not the contact.
	const isDealRecord = Boolean(response.salesforceOpportunityId || response.salesforceLeadId);
	const contactUrl = isDealRecord ? null : (response.recordUrl ?? null);
	const records: MappingTestSyncOutcome["records"] = [];

	if (response.salesforceContactId) {
		records.push({
			label: ts("preview.sync.contactRecord", { id: response.salesforceContactId }),
			url: contactUrl,
		});
	}

	if (response.salesforceAccountId) {
		records.push({
			label: ts("preview.sync.accountRecord", { id: response.salesforceAccountId }),
			url: null,
		});
	}

	if (response.salesforceLeadId) {
		records.push({
			label: ts("preview.sync.leadRecord", { id: response.salesforceLeadId }),
			url: response.recordUrl ?? null,
		});
	}

	if (response.salesforceOpportunityId) {
		records.push({
			label: ts("preview.sync.opportunityRecord", { id: response.salesforceOpportunityId }),
			url: response.recordUrl ?? null,
		});
	}

	return { success: response.success, error: describeTestSyncError(response, ts), records };
};

/**
 * The rejection text the modal shows.
 *
 * Salesforce returns rejections on the DTO rather than throwing, and the machine-readable
 * `errorCode` is the actionable half — a `DUPLICATES_DETECTED` sends the customer to their
 * duplicate rules rather than to their mappings, which the free-text message alone does not
 * make obvious. The named `fields` come along because a `REQUIRED_FIELD_MISSING` is only
 * useful if you know which field.
 */
const describeTestSyncError = (response: SalesforceTestSyncResponse, ts: Ts): string | null => {
	if (response.success) {
		return null;
	}

	const parts: string[] = [];

	if (response.errorCode) {
		parts.push(response.errorCode);
	}

	if (response.errorFields?.length) {
		parts.push(`(${response.errorFields.join(", ")})`);
	}

	if (response.error) {
		parts.push(response.error);
	}

	if (response.errorCode === "DUPLICATES_DETECTED" || response.errorCode === "DUPLICATE_VALUE") {
		parts.push(ts("preview.sync.duplicateHint"));
	}

	return parts.length > 0 ? parts.join(" ") : null;
};
